/**
 * Prototype-only: what the statement preview's Download button does while the
 * revenue lines (CSV) can't be downloaded — still being prepared, or a legacy
 * statement that never gets one.
 *
 *  - by-files — only what can be downloaded is offered: a plain Download
 *    button for the PDF, with no chevron, and no CSV option in the list row's
 *    actions menu. Both appear once the CSV is ready (the default).
 *  - disabled-note — always the Download ▾ menu and the row's CSV option, with
 *    the CSV disabled and a note under it saying why.
 *
 * Persisted, so a refresh keeps the version the reviewer picked.
 *
 * Scaffolding for the statement-download prototype — delete with the rest of
 * src/prototypes/statement-download/.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { RoyaltyStatement } from "@/types";

export const DOWNLOAD_VARIANTS = {
  "by-files": "Chevron by files",
  "disabled-note": "Disabled + note",
} as const;

export type DownloadVariant = keyof typeof DOWNLOAD_VARIANTS;

type DownloadVariantState = {
  downloadVariant: DownloadVariant;
  setDownloadVariant: (variant: DownloadVariant) => void;
};

export const useDownloadVariant = create<DownloadVariantState>()(
  persist(
    (set) => ({
      downloadVariant: "by-files",
      setDownloadVariant: (downloadVariant) => set({ downloadVariant }),
    }),
    // v3: earlier versions stored variants that no longer exist.
    { name: "prototype-statement-download-variant-v3" },
  ),
);

/** Whether a statement's menus offer the CSV at all — working, or disabled with the reason. */
export function useOffersRevenueCsv(statement: RoyaltyStatement): boolean {
  const { downloadVariant } = useDownloadVariant();
  return statement.revenue_csv_status === "ready" || downloadVariant === "disabled-note";
}
